import React from 'react';
import { RadioProps } from '../../../types/DesignSystem/Radio';
import Typography from '../Typography/Typography';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';

/**
 * Radio.
 *
 * Built on a real `<input type="radio">`, for the same reason as `Checkbox`: the
 * previous `<div role="radio">` had an `onClick` and no `tabIndex`, so it was
 * unreachable by keyboard.
 *
 * **Pass `name` to group radios.** Arrow-key navigation between options, and the
 * single-tab-stop behaviour the ARIA APG specifies for a radio group, are things
 * the browser provides only for inputs that share a `name`. Mutual exclusivity
 * still works without it (`checked` is controlled), so a missing `name` degrades
 * quietly rather than visibly — which is exactly why it is worth stating here.
 */
const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio({
  label,
  hideLabel = false,
  value,
  checked,
  onChange,
  disabled = false,
  size = 'md',
  className = '',
  name,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  ...rest
}, ref) {
  /*
   * § 279 — the same two rungs `Checkbox` has, derived the same way, so the pair
   * stays a family. The DOT scales with the box (8px in the 20px control, 6px in
   * the 16px one) rather than staying fixed, which would make the small radio read
   * as a filled disc instead of a ring with a mark in it.
   */
  const boxSize = size === 'sm' ? 'tw-w-4 tw-h-4' : 'tw-w-5 tw-h-5';
  const dotSize = size === 'sm' ? 'tw-w-1.5 tw-h-1.5' : 'tw-w-2 tw-h-2';
  /* Checkbox's own nudge: at `sm` the control is 16px against a 20px line box, so
     it needs half a step down to sit on the text's optical centre. */
  const boxNudge = size === 'sm' ? 'tw-mt-0.5' : '';
  return (
    <label
      /*
        `items-start`, matching Checkbox — not `items-center`.
        The 20px control and `body-primary`'s 20px line box centre on each other
        already, so a ONE-LINE label renders identically either way. The difference
        only shows on a label that wraps or carries a description, where centring
        floats the control against the middle of a paragraph. Aligning with Checkbox
        also matters because the two sit in the same form and are meant to read as
        one family.
      */
      className={withClassName(`tw-flex tw-items-start tw-gap-3 ${
        disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
      }`, className)}
    >
      <span
        className={`tw-relative tw-flex-shrink-0 ${boxNudge}`}
        data-testid={dataTestId ? `${dataTestId}-box` : undefined}
      >
        <input
          /* rest first — see the rule in Button.tsx. */
          {...rest}
          ref={ref}
          type="radio"
          role="radio"
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(value, event)}
          /* See Checkbox: the wrapping `<label>` names this, and an `aria-label`
             would override that text rather than add to it. */
          aria-label={ariaLabel}
          data-testid={dataTestId}
          /* `forced-colors:tw-appearance-auto` (§ 224) — the Checkbox reasoning:
             the checked fill is stripped in Windows High Contrast, so the OS
             draws the native dot instead; the overlay hides itself there. */
          className={`${boxSize} tw-block tw-m-0 tw-appearance-none forced-colors:tw-appearance-auto tw-border tw-rounded-full tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${
            // Fills when selected, with a white centre — the same treatment as
            // Checkbox, so the two read as one family when they sit in the same
            // form. 1px border, matching Checkbox: 2px here and 1px there was a
            // visible mismatch between two controls that appear side by side.
            //
            // § 227 — disabled recedes by token, not the old `tw-opacity-50` wash:
            // Checkbox's exact pairs (Toggle § 123 doctrine), so the two controls
            // agree about what "locked" looks like too.
            disabled
              ? checked
                ? 'tw-bg-ink-disabled tw-border-ink-disabled'
                : 'tw-bg-surface-sunken tw-border-edge'
              : checked
                ? 'tw-bg-accent-bold tw-border-accent-bold'
                : 'tw-bg-surface tw-border-edge-strong'
          } ${
            disabled
              ? 'tw-cursor-not-allowed'
              : checked
                ? 'tw-cursor-pointer'
                : // hover only while empty — tinting a filled control is wrong
                  'tw-cursor-pointer hover:tw-bg-accent-tint hover:tw-border-accent'
          } ${FOCUS_RING}`}
        />
        <span
          aria-hidden="true"
          /* Hidden under forced colors (§ 224) — the native input draws the dot. */
          className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none forced-colors:tw-hidden"
        >
          {/* § 219 — same mark entrance as Checkbox: the two sit in one form and
              must agree about how "on" arrives. § 227 — and the same disabled glyph
              ink: `ink-secondary` on the `ink-disabled` fill. */}
          {checked && <span className={`tw-block ${dotSize} ${disabled ? 'tw-bg-ink-secondary' : 'tw-bg-ink-inverse'} tw-rounded-full motion-safe:tw-animate-mark-pop`} />}
        </span>
      </span>
      {/*
        No `label.length` test: `label` is a ReactNode now, and `.length` on an
        element is `undefined`, so the old guard silently rendered nothing for
        every non-string label. A nullish/empty-string check is what it always
        meant.
      */}
      {label !== null && label !== undefined && label !== '' && (
        <span
          /* § 355 — hidden, not removed: text inside the `<label>` stays the option's name. */
          className={hideLabel ? 'tw-sr-only' : 'tw-min-w-0 tw-flex-1'}
        >
          {/* § 227 — the label recedes with its control, Checkbox's rule verbatim. */}
          {/* § 346 — `as="span"` + `block`: a `<p>` (the variant's default) inside a
              `<label>` is invalid, and the block box is what it always rendered as. */}
          <Typography variant="body-primary" as="span" className={`tw-block ${disabled ? 'tw-text-ink-disabled' : 'tw-text-ink'}`}>
            {label}
          </Typography>
        </span>
      )}
    </label>
  );
});

export default Radio;
